/**
 * CollapsibleCitations — the shared "Show/Hide sources (N)" disclosure used by
 * every agent message that carries citations (normal chat bubbles AND the
 * inbound-summary hand-off card). Extracted from MessageBubble so the toggle
 * vocabulary (chevron, collapsed-only count badge, amber treatment, expand
 * animation) stays identical across surfaces instead of drifting per-call-site.
 *
 * Every citation the message carries is a chip, and the count is the number of
 * chips shown. The only set the count differs from is the raw citations array:
 * tools/registry.go flattens per tool call, so one chunk reached twice arrives
 * twice, and the chunk-identity dedup below collapses those to one.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CitationResponse } from '@/api/types'
import { CitationBadge } from './CitationBadge'

/**
 * Identity of the chunk a citation points at. tools/registry.go flattens per
 * tool call, so two tools that both surface page 8 produce two citations for
 * one source — "1 source" above two identical chips reads as two.
 *
 * The chunk id is that identity where the citation carries one. A medication
 * lookup returns one citation per Fachinformation section, and every section of
 * one product shares a document, so keying on the document alone collapses two
 * cited sections into one chip and hides half of what the answer rests on. The
 * document and page key still identifies a citation that names no passage: a
 * terminology, dm+d or compatibility result, and any row written before the
 * field existed.
 */
function chunkKey(citation: CitationResponse): string {
  if (citation.chunk_id) {
    return citation.chunk_id
  }
  return `${citation.document}|${citation.page_start}|${citation.page_end}`
}

/**
 * Collapses citations that point at the same chunk, keeping first-seen order.
 */
function distinctChunks(citations: CitationResponse[]): CitationResponse[] {
  const byChunk = new Map<string, CitationResponse>()
  for (const citation of citations) {
    const key = chunkKey(citation)
    if (!byChunk.has(key)) {
      byChunk.set(key, citation)
    }
  }
  return [...byChunk.values()]
}

export function CollapsibleCitations({
  messageId,
  citations,
  onCitationClick,
}: {
  messageId: string
  citations: CitationResponse[]
  onCitationClick?: (citation: CitationResponse) => void
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const distinct = distinctChunks(citations)

  if (!distinct.length) return null

  return (
    <div className="mt-3 pt-3 border-t border-neutral-200/30">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex items-center gap-1.5 text-sm',
          'text-citation-600 hover:text-citation-800',
          'transition-colors',
        )}
        aria-expanded={isExpanded}
        aria-controls={`citations-${messageId}`}
      >
        {isExpanded ? (
          <>
            <ChevronUp className="w-3.5 h-3.5" />
            {t('chat.hideSources')}
          </>
        ) : (
          <>
            <ChevronDown className="w-3.5 h-3.5" />
            {t('chat.showSources')}
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-citation-100 text-citation-700 rounded-full">
              {distinct.length}
            </span>
          </>
        )}
      </button>

      {isExpanded && (
        <div
          id={`citations-${messageId}`}
          className="mt-2 flex flex-wrap gap-1.5 animate-expand"
        >
          {distinct.map((citation) => (
            <CitationBadge
              key={chunkKey(citation)}
              citation={citation}
              onClick={() => onCitationClick?.(citation)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
